import { format } from "date-fns";

import { DateOption, DateRange } from "@zapier/design-system";

const date = new Date();
const year = date.getFullYear();
const month = date.getMonth();
const day = date.getDate();

export enum DateRangeLabel {
  CurrentUsagePeriod = "Current usage period",
  LastMonth = "Last month",
  MonthToDate = "This month",
  Past30Days = "Last 30 days",
}

export const getDateOptions = (currentUsagePeriod: DateRange): DateOption[] => [
  {
    key: DateRangeLabel.Past30Days,
    text: DateRangeLabel.Past30Days,
    value: {
      from: new Date(year, month, day - 29),
      to: date,
    },
  },
  {
    key: DateRangeLabel.CurrentUsagePeriod,
    text: DateRangeLabel.CurrentUsagePeriod,
    value: currentUsagePeriod,
  },
  {
    key: DateRangeLabel.MonthToDate,
    text: DateRangeLabel.MonthToDate,
    value: {
      from: new Date(year, month, 1),
      to: date,
    },
  },
  {
    key: DateRangeLabel.LastMonth,
    text: DateRangeLabel.LastMonth,
    value: {
      from: new Date(year, month - 1, 1),
      to: new Date(year, month, 0),
    },
  },
];

/**
 * Returns an ISO formatted date string:
 * - with only the date (no time)
 * - in the same timezone as the input date (not converted to UTC)
 */
function getFormattedDate(date: Date | string | null | undefined) {
  if (!date) {
    return undefined;
  }

  return format(new Date(date), "yyyy-MM-dd");
}

export function dateRangeLabel(
  startDate: Date | string | undefined,
  endDate: Date | string | undefined,
  dateOptions: DateOption[]
): string {
  // Test against getDateOptions values to see if we can find a match. Note, we don't use time, only date.
  const formattedStartDate = getFormattedDate(startDate);
  const formattedEndDate = getFormattedDate(endDate);

  if (!formattedStartDate || !formattedEndDate) {
    return "";
  }

  const matchingOption = dateOptions.find(
    (option) =>
      option.value.from &&
      getFormattedDate(option.value.from) === formattedStartDate &&
      option.value.to &&
      getFormattedDate(option.value.to) === formattedEndDate
  );

  return matchingOption?.key ?? "";
}

export default getDateOptions;
